import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { API_URL } from '../config';

export default function OperatorVerification({ isDarkMode, onClose, companies }) {
  const [activeTab, setActiveTab] = useState('export');
  const [exportData, setExportData] = useState('');
  const [importData, setImportData] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [operatorMappings, setOperatorMappings] = useState({});
  const [csvData, setCsvData] = useState([]); // CSV data ze souboru
  const [previewData, setPreviewData] = useState([]); // Náhled změn pro import
  const [showPreview, setShowPreview] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');

  // Funkce pro export telefonních čísel
  const handleExport = () => {
    const phoneNumbers = [];
    
    companies.forEach(company => {
      if (company.contacts && company.contacts.length > 0) {
        company.contacts.forEach(contact => {
          if (contact.phone) {
            // Normalizace telefonního čísla - odstranění všech ne-číselných znaků
            let normalizedPhone = contact.phone.replace(/[^\d]/g, '');
            
            // Přidání 420 pokud číslo nezačíná 420 a má délku 9 čísel
            if (normalizedPhone.length === 9 && !normalizedPhone.startsWith('420')) {
              normalizedPhone = '420' + normalizedPhone;
            }
            
            // Přidání pouze pokud je číslo validní (má délku 12 a začíná 420)
            if (normalizedPhone.length === 12 && normalizedPhone.startsWith('420')) {
              phoneNumbers.push(normalizedPhone);
            }
          }
        });
      }
    });

    // Odstranění duplikátů
    const uniquePhones = [...new Set(phoneNumbers)];
    
    // Vytvoření řetězce oddělovaného čárkami
    const exportText = uniquePhones.join(',');
    
    setExportData(exportText);
    
    // Automatické stažení do .txt souboru
    const blob = new Blob([exportText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `telefonni_cisla_export_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Funkce pro načtení CSV nebo Excel souboru
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCsvFile(file);
      parseFile(file);
    }
  };

  // Funkce pro parsování CSV nebo Excel souboru
  const parseFile = (file) => {
    const fileName = file.name.toLowerCase();
    
    if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
      // Excel soubor
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const arrayBuffer = e.target.result;
          const workbook = XLSX.read(arrayBuffer, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          if (jsonData.length === 0) {
            alert('Excel soubor je prázdný');
            return;
          }
          
          const parsedData = [];
          
          // Předpokládáme formát: MSISDN,Operator
          jsonData.forEach((row, index) => {
            if (index === 0 && (String(row[0]).includes('MSISDN') || String(row[1]).includes('Operator'))) {
              // Přeskočit hlavičku
              return;
            }
            
            const msisdn = row[0] ? String(row[0]).trim() : '';
            const operator = row[1] ? String(row[1]).trim() : '';
            
            if (msisdn && operator) {
              // Sjednotit názvy sloupců s CSV parserem (velká písmena)
              parsedData.push({ MSISDN: msisdn, Operator: operator });
            }
          });

          setCsvData(parsedData);
          
          // Automatické vytvoření mapování operátorů
          const mappings = {};
          const uniqueOperators = [...new Set(parsedData.map(item => item.Operator))];
          
          uniqueOperators.forEach(op => {
            mappings[op] = op; // Defaultně mapuje sám na sebe
          });
          
          setOperatorMappings(mappings);
          
          // Zobrazení import dat jako text pro kontrolu
          const importText = parsedData.map(item => `${item.MSISDN},${item.Operator}`).join('\n');
          setImportData(`MSISDN,Operator\n${importText}`);
          
        } catch (error) {
          alert(`Chyba při parsování Excel souboru: ${error.message}`);
        }
      };
      reader.readAsArrayBuffer(file);
    } else if (fileName.endsWith('.csv')) {
      // CSV soubor
      const reader = new FileReader();
      reader.onload = (e) => {
        setImportData(e.target.result);
        parseCSVData(e.target.result);
      };
      reader.readAsText(file);
    } else {
      alert('Nepodporovaný formát souboru. Použijte .xlsx, .xls nebo .csv');
    }
  };

  // Funkce pro parsování CSV dat
  const parseCSVData = (csvText) => {
    const lines = csvText.split('\n').filter(line => line.trim());
    const parsedData = [];
    
    // Předpokládáme formát: MSISDN,Operator
    lines.forEach((line, index) => {
      if (index === 0 && (line.includes('MSISDN') || line.includes('Operator'))) {
        // Přeskočit hlavičku
        return;
      }
      
      const [msisdn, operator] = line.split(',').map(item => item.trim().replace(/"/g, ''));
      if (msisdn && operator) {
        // Sjednotit názvy sloupců s Excel parserem (velká písmena)
        parsedData.push({ MSISDN: msisdn, Operator: operator });
      }
    });

    setCsvData(parsedData);
    
    // Automatické vytvoření mapování operátorů
    const mappings = {};
    const uniqueOperators = [...new Set(parsedData.map(item => item.Operator))];
    
    uniqueOperators.forEach(op => {
      // Automatické mapování na kratší názvy
      if (op.toLowerCase().includes('vodafone')) {
        mappings[op] = 'Vodafone';
      } else if (op.toLowerCase().includes('t-mobile') || op.toLowerCase().includes('tmobile')) {
        mappings[op] = 'T-Mobile';
      } else if (op.toLowerCase().includes('o2')) {
        mappings[op] = 'O2';
      } else {
        mappings[op] = op; // Ponechat původní název
      }
    });
    
    setOperatorMappings(mappings);
  };

  // Funkce pro aktualizaci mapování operátorů
  const updateOperatorMapping = (originalName, newName) => {
    setOperatorMappings(prev => ({
      ...prev,
      [originalName]: newName
    }));
  };

  // Funkce pro náhled před importem
  const showImportPreview = async () => {
    console.log('🚀 showImportPreview clicked');
    console.log('csvData length:', csvData.length);
    console.log('csvData sample:', csvData.slice(0, 2));
    console.log('operatorMappings:', operatorMappings);
    
    if (csvData.length === 0) {
      console.error('❌ CSV data je prázdné');
      alert('Nejdříve nahrajte Excel nebo CSV soubor');
      return;
    }

    // OPTIMALIZACE: Použít přímo firmy z Contact Managementu (už načtené v paměti)
    console.log(`� Použití firem z Contact Managementu: ${companies.length} firem`);
    const companiesToMatch = companies;
    
    if (!companiesToMatch || companiesToMatch.length === 0) {
      console.error('❌ Žádné firmy k dispozici v Contact Managementu');
      alert('Nejsou načtené žádné firmy. Nejdříve otevřete firmy v Contact Managementu.');
      return;
    }
    
    console.log(`✅ Použito ${companiesToMatch.length} firem z Contact Managementu`);

    // Debug vzorky dat před zpracováním
    console.log('🔍 Vzorky dat před zpracováním:');
    console.log(`CSV vzorky (prvních 3):`, csvData.slice(0, 3));
    
    // Najit první pár firem s kontakty
    const companiesWithContacts = companiesToMatch.filter(c => c.contacts && c.contacts.length > 0).slice(0, 2);
    console.log(`Firemní kontakty vzorky:`, companiesWithContacts.map(c => ({
      name: c.name,
      contacts: c.contacts.map(contact => ({ name: contact.name, phone: contact.phone }))
    })));

    setIsProcessing(true);
    setLoadingProgress(0);
    setLoadingMessage('Připravuji analýzu dat...');

    try {
      // OPTIMALIZACE: Vytvoření hash mapy telefon → [firma + kontakt] pro rychlé vyhledávání
      console.log('🔧 Vytvářím hash mapu telefonů...');
      setLoadingMessage('Indexuji telefonní čísla...');
      
      const phoneToCompanyMap = new Map(); // normalizované číslo → [{company, contact}, ...]
      const companyOperatorMap = {}; // Mapa firma_id → nalezený operátor
      const changesPreview = []; // Pole pro náhled změn
      
      companiesToMatch.forEach((company, companyIndex) => {
        if (company.contacts && company.contacts.length > 0) {
          company.contacts.forEach(contact => {
            if (contact.phone) {
              // Normalizace čísla z databáze
              let normalizedContactPhone = String(contact.phone).replace(/[^\d]/g, '');
              
              if (normalizedContactPhone.length === 9) {
                normalizedContactPhone = '420' + normalizedContactPhone;
              }
              
              // Uložit do mapy - může být více firem se stejným číslem
              if (!phoneToCompanyMap.has(normalizedContactPhone)) {
                phoneToCompanyMap.set(normalizedContactPhone, []);
              }
              phoneToCompanyMap.get(normalizedContactPhone).push({ company, contact });
              
              // Přidat i variantu bez 420 pro lepší matching
              if (normalizedContactPhone.startsWith('420') && normalizedContactPhone.length === 12) {
                const without420 = normalizedContactPhone.substring(3);
                if (!phoneToCompanyMap.has(without420)) {
                  phoneToCompanyMap.set(without420, []);
                }
                phoneToCompanyMap.get(without420).push({ company, contact });
              }
            }
          });
        }
        
        // Progress update každých 100 firem
        if (companyIndex % 100 === 0) {
          setLoadingProgress(Math.floor((companyIndex / companiesToMatch.length) * 30)); // 0-30%
        }
      });
      
      console.log(`✅ Hash mapa vytvořena: ${phoneToCompanyMap.size} unikátních čísel`);
      
      // Průchod CSV dat - nyní rychlé vyhledávání v hash mapě
      setLoadingMessage('Párování CSV čísel s firmami...');
      let foundMatches = 0;
      
      console.log(`🔍 Začínám rychlé párování ${csvData.length} CSV záznamů pomocí hash mapy`);
      
      for (let i = 0; i < csvData.length; i++) {
        const item = csvData[i];
        
        // Progress update každých 100 záznamů
        if (i % 100 === 0) {
          setLoadingProgress(30 + Math.floor((i / csvData.length) * 40)); // 30-70%
          await new Promise(resolve => setTimeout(resolve, 1)); // Dát prohlížeči čas
        }

        // Normalizace MSISDN z CSV
        let normalizedMsisdn = String(item.MSISDN || item.msisdn || '').replace(/[^\d]/g, '');
        
        if (normalizedMsisdn.length === 9) {
          normalizedMsisdn = '420' + normalizedMsisdn;
        }
        
        // Debug prvních 5 záznamů
        if (i < 5) {
          console.log(`CSV záznam ${i}:`, (item.MSISDN || item.msisdn), '->', normalizedMsisdn, `(délka: ${normalizedMsisdn.length})`);
        }

        // RYCHLÉ VYHLEDÁNÍ v hash mapě - O(1) místo O(n×m)
        const matches = phoneToCompanyMap.get(normalizedMsisdn);
        
        if (matches && matches.length > 0) {
          foundMatches++;
          const newOperator = operatorMappings[item.Operator] || item.Operator;
          
          // Debug prvních 5 shod
          if (foundMatches <= 5) {
            console.log(`✅ Shoda ${foundMatches}:`, 
              `CSV: ${(item.MSISDN || item.msisdn)} -> ${normalizedMsisdn}`, 
              `Firem: ${matches.length}`,
              `operátor: ${item.Operator} -> ${newOperator}`
            );
          }
          
          // Zpracovat všechny nalezené firmy (obvykle 1, ale může být víc)
          matches.forEach(({ company, contact }) => {
            // Uložit operátora pro celou firmu
            if (!companyOperatorMap[company._id]) {
              companyOperatorMap[company._id] = {
                companyName: company.name,
                companyId: company._id,
                currentOperator: company.operator || 'Nepřiřazeno',
                newOperator: newOperator,
                matchedPhones: [],
                allPhones: []
              };
            }
            
            // Přidat telefonní číslo, které se shoduje s CSV (pokud ještě není)
            if (!companyOperatorMap[company._id].matchedPhones.some(p => p.phone === contact.phone)) {
              companyOperatorMap[company._id].matchedPhones.push({
                contactName: contact.name,
                phone: contact.phone,
                source: 'CSV'
              });
            }
          });
        }
      }

      // Statistiky
      console.log(`📊 Statistiky zpracování:`);
      console.log(`- CSV záznamů: ${csvData.length}`);
      console.log(`- Firem celkem (all accounts): ${companiesToMatch.length}`);
      console.log(`- Unikátních čísel v hash mapě: ${phoneToCompanyMap.size}`);
      console.log(`- Nalezených shod: ${foundMatches}`);
      console.log(`- Ovlivněných firem: ${Object.keys(companyOperatorMap).length}`);
      
      // Debug: Výpis prvních pár CSV čísel vs prvních pár firemních kontaktů
      if (foundMatches === 0) {
        console.log(`🔍 Debug - žádné shody nenalezeny, kontroluji formáty:`);
        console.log(`CSV vzorky (prvních 3):`, csvData.slice(0, 3).map(item => ({ 
          raw: item.MSISDN || item.msisdn,
          normalized: String(item.MSISDN || item.msisdn || '').replace(/[^\d]/g, ''),
          operator: item.Operator || item.operator
        })));
        
        const samplesCompanies = companiesToMatch.filter(c => c.contacts && c.contacts.length > 0).slice(0, 2);
        console.log(`Kontakty vzorky (prvních 2 firem):`, samplesCompanies.map(c => ({
          company: c.name,
          contacts: c.contacts.slice(0, 3).map(contact => ({
            name: contact.name,
            raw: contact.phone,
            normalized: String(contact.phone || '').replace(/[^\d]/g, '')
          }))
        })));
      }

      // Druhý průchod - přidat všechna čísla u firem, které mají nějaký operátor
      setLoadingMessage('Zpracovávám kontakty firem...');
      const companyIds = Object.keys(companyOperatorMap);
      for (let i = 0; i < companyIds.length; i++) {
        const companyId = companyIds[i];

        if (i % 50 === 0) {
          setLoadingProgress(70 + Math.floor((i / companyIds.length) * 20)); // 70-90%
          await new Promise(resolve => setTimeout(resolve, 1));
        }

        const company = companiesToMatch.find(c => c._id === companyId);
        if (company && company.contacts) {
          company.contacts.forEach(contact => {
            if (contact.phone && !companyOperatorMap[companyId].matchedPhones.some(p => p.phone === contact.phone)) {
              companyOperatorMap[companyId].allPhones.push({
                contactName: contact.name || 'Bez jména',
                phone: contact.phone,
                source: 'Systém'
              });
            }
          });
        }
      }

      // Finální zpracování - vytvoření preview dat
      setLoadingMessage('Připravuji náhled...');
      setLoadingProgress(90);
      const companies_data = Object.entries(companyOperatorMap);
      
      for (let i = 0; i < companies_data.length; i++) {
        const [companyId, companyData] = companies_data[i];

        // Přidat hlavní záznam pro firmu
        changesPreview.push({
          isMainEntry: true,
          companyId: companyData.companyId,
          companyName: companyData.companyName,
          contactName: `📱 Nalezeno v CSV: ${companyData.matchedPhones.length} čísel`,
          phone: companyData.matchedPhones.map(p => p.phone).join(', '),
          currentOperator: companyData.currentOperator,
          newOperator: companyData.newOperator
        });

        // Přidat všechna čísla (hlavní i doplňková)
        [...companyData.matchedPhones, ...companyData.allPhones].forEach(phoneData => {
          changesPreview.push({
            isMainEntry: false,
            companyId: companyData.companyId,
            companyName: companyData.companyName,
            contactName: phoneData.contactName,
            phone: phoneData.phone,
            currentOperator: companyData.currentOperator,
            newOperator: companyData.newOperator,
            source: phoneData.source
          });
        });
      }

      // Finalizace
      setLoadingProgress(100);
      setLoadingMessage('Dokončeno!');
      await new Promise(resolve => setTimeout(resolve, 300));

      // Seskupit změny podle firem (pouze hlavní záznamy)
      const groupedChanges = changesPreview.filter(item => item.isMainEntry);
      
      console.log(`Náhled připraven: ${groupedChanges.length} firem bude mít změněný operátor`);

      console.log('📊 Preview data prepared:', changesPreview.length, 'items');
      console.log('📊 Grouped changes (main entries):', groupedChanges.length, 'companies');
      console.log('📊 First few preview items:', changesPreview.slice(0, 3));
      console.log('📊 foundMatches celkem:', foundMatches);
      
      if (groupedChanges.length === 0) {
        console.error('❌ Žádné shody nalezeny!');
        console.log('Debug info:');
        console.log('- CSV data vzorky:', csvData.slice(0, 3));
        console.log('- Companies s kontakty vzorky:', companiesToMatch.filter(c => c.contacts?.length > 0).slice(0, 2).map(c => ({
          name: c.name,
          contacts: c.contacts.map(contact => contact.phone)
        })));
        alert('Nenalezeny žádné shody mezi CSV daty a kontakty ve firmách. Zkontrolujte formát telefonních čísel.');
        return; // Ukončit zde, neukládat prázdný preview
      }
      
      // Uložení náhledu pro zobrazení pouze pokud jsou shody
      setPreviewData(changesPreview);
      setShowPreview(true);
      
      // Debug info po uložení
      setTimeout(() => {
        console.log('✅ State updated - showPreview should be true');
        console.log('✅ Preview data length:', changesPreview.length);
      }, 100);
      
    } catch (error) {
      console.error('Chyba při vytváření náhledu:', error);
      alert('Chyba při vytváření náhledu: ' + error.message);
    } finally {
      setIsProcessing(false);
      setLoadingProgress(0);
      setLoadingMessage('');
    }
  };

  // Funkce pro provedení importu
  const executeImport = async () => {
    if (!showPreview || previewData.length === 0) {
      alert('Nejdříve zobrazte náhled změn');
      return;
    }

    setIsProcessing(true);
    setLoadingProgress(0);
    setLoadingMessage('Zahajuji import...');

    try {
      const companyUpdates = {};

      // Seskupit změny podle firem (pouze hlavní záznamy)
      previewData.forEach(change => {
        if (change.isMainEntry && !companyUpdates[change.companyId]) {
          companyUpdates[change.companyId] = {
            companyId: change.companyId,
            companyName: change.companyName,
            newOperator: change.newOperator
          };
        }
      });

      const updates = Object.values(companyUpdates);
      const totalUpdates = updates.length;

      console.log('Pripravuji bulk update pro', totalUpdates, 'firem');

      // Rozdeleni na davky po 5000 firmach pro frontend batching
      const FRONTEND_BATCH_SIZE = 5000;
      const batches = [];
      for (let i = 0; i < updates.length; i += FRONTEND_BATCH_SIZE) {
        batches.push(updates.slice(i, i + FRONTEND_BATCH_SIZE));
      }

      console.log('Rozdeleno na', batches.length, 'frontend davek po max', FRONTEND_BATCH_SIZE, 'firem');

      let totalModified = 0;

      // Postupne zpracovat davky
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        
        setLoadingMessage(`Aktualizuji davku ${batchIndex + 1}/${batches.length} (${batch.length} firem)...`);
        setLoadingProgress(Math.floor(((batchIndex) / batches.length) * 100));

        console.log('Posilam davku', batchIndex + 1, '/', batches.length, '(', batch.length, 'firem)');

        // BULK UPDATE - jeden API call pro davku firem
        const response = await fetch(`${API_URL}/companies/bulk-update-operators`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ updates: batch })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `HTTP ${response.status}`);
        }

        const result = await response.json();
        totalModified += result.modified;
        
        console.log('Davka', batchIndex + 1, 'dokoncena:', result.modified, 'firem aktualizovano');
      }
      
      setLoadingProgress(100);
      setLoadingMessage('Import dokoncen!');
      await new Promise(resolve => setTimeout(resolve, 500));

      console.log('Celkem aktualizovano:', totalModified, 'firem');

      let alertMessage = `Import dokoncen!\nUspesne aktualizovano: ${totalModified} firem`;
      alertMessage += `\nCelkem zpracovano: ${totalUpdates} firem`;
      
      alert(alertMessage);
      
      // Reset stavu
      setImportData('');
      setCsvFile(null);
      setPreviewData([]);
      setShowPreview(false);
      setOperatorMappings({});

    } catch (error) {
      console.error('Chyba při importu:', error);
      alert('Došlo k chybě při importu dat: ' + error.message);
    } finally {
      setIsProcessing(false);
      setLoadingProgress(0);
      setLoadingMessage('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto custom-scrollbar ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        {/* Header */}
        <div className={`sticky top-0 border-b p-4 flex justify-between items-center rounded-t-lg ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div>
            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              📞 Ověření operátorů
            </h2>
            <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Pracuji s {companies.length} firmami z Contact Managementu
            </div>
          </div>
          <button
            onClick={onClose}
            className={`text-2xl leading-none ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
          >
            ×
          </button>
        </div>

        {/* Tab Navigation */}
        <div className={`flex border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={() => setActiveTab('export')}
            className={`flex-1 p-4 text-center font-semibold transition-colors ${
              activeTab === 'export'
                ? isDarkMode 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-blue-100 text-blue-800 border-b-2 border-blue-500'
                : isDarkMode 
                  ? 'text-gray-300 hover:bg-gray-700' 
                  : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            📤 Export
          </button>
          <button
            onClick={() => setActiveTab('import')}
            className={`flex-1 p-4 text-center font-semibold transition-colors ${
              activeTab === 'import'
                ? isDarkMode 
                  ? 'bg-green-600 text-white' 
                  : 'bg-green-100 text-green-800 border-b-2 border-green-500'
                : isDarkMode 
                  ? 'text-gray-300 hover:bg-gray-700' 
                  : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            📥 Import
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'export' && (
            <div className="space-y-6">
              <div>
                <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Export telefonních čísel
                </h3>
                <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Exportuje všechna telefonní čísla zákazníků v CRM do .txt souboru ve formátu vhodném pro Vodafone.
                </p>
                
                <button
                  onClick={handleExport}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  📤 Exportovat telefonní čísla
                </button>

                {exportData && (
                  <div className="mt-6">
                    <h4 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      Náhled exportovaných dat:
                    </h4>
                    <div className={`p-4 rounded-lg font-mono text-sm max-h-40 overflow-y-auto ${isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'}`}>
                      {exportData.split(',').slice(0, 10).join(',')}{exportData.split(',').length > 10 && '...'}
                    </div>
                    <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Celkem: {exportData.split(',').length} telefonních čísel
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'import' && (
            <div className="space-y-6">
              <div>
                <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Import operátorů z CSV
                </h3>
                <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Nahrajte Excel (.xlsx, .xls) nebo CSV soubor ve formátu MSISDN,Operator pro automatické přiřazení operátorů k zákazníkům.
                </p>

                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Soubor:
                    </label>
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileUpload}
                      className={`block w-full text-sm border rounded-lg p-2 ${
                        isDarkMode 
                          ? 'bg-gray-700 border-gray-600 text-gray-200' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    />
                  </div>

                  {csvData.length > 0 && (
                    <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-blue-900/20 text-blue-200' : 'bg-blue-50 text-blue-800'}`}>
                      <div className="flex items-center">
                        <span className="mr-2">✅</span>
                        <span className="text-sm">
                          Načteno <strong>{csvData.length}</strong> záznamů ze souboru
                        </span>
                      </div>
                    </div>
                  )}

                  {Object.keys(operatorMappings).length > 0 && (
                    <div>
                      <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Mapování operátorů:
                      </h4>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {Object.entries(operatorMappings).map(([original, mapped]) => (
                          <div key={original} className="flex items-center space-x-3">
                            <span className={`flex-1 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              {original} →
                            </span>
                            <input
                              type="text"
                              value={mapped}
                              onChange={(e) => updateOperatorMapping(original, e.target.value)}
                              className={`flex-1 text-sm border rounded px-2 py-1 ${
                                isDarkMode 
                                  ? 'bg-gray-700 border-gray-600 text-gray-200' 
                                  : 'bg-white border-gray-300 text-gray-900'
                              }`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex space-x-3">
                    <button
                      onClick={showImportPreview}
                      disabled={!importData || isProcessing}
                      className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                        !importData || isProcessing
                          ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                          : 'bg-orange-600 hover:bg-orange-700 text-white'
                      }`}
                    >
                      👀 Zobrazit náhled
                    </button>
                    
                    <button
                      onClick={executeImport}
                      disabled={!showPreview || isProcessing}
                      className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                        !showPreview || isProcessing
                          ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                          : 'bg-green-600 hover:bg-green-700 text-white'
                      }`}
                    >
                      {isProcessing ? '⏳ Zpracovávám...' : '✅ Provést import'}
                    </button>
                  </div>
                </div>

                {showPreview && previewData.length > 0 && (
                  <div>
                    <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      Náhled změn ({previewData.filter(item => item.isMainEntry).length} firem):
                    </h4>
                    <div className={`rounded-lg max-h-80 overflow-y-auto ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <table className="w-full text-sm">
                        <thead className={`sticky top-0 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                          <tr>
                            <th className={`p-2 text-left ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Firma / Kontakt</th>
                            <th className={`p-2 text-left ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Telefon</th>
                            <th className={`p-2 text-left ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Současný operátor</th>
                            <th className={`p-2 text-left ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Nový operátor</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.slice(0, 100).map((item, index) => (
                            <tr 
                              key={index} 
                              className={`border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-200'} ${
                                item.isMainEntry 
                                  ? isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50' 
                                  : isDarkMode ? 'bg-gray-800/50' : 'bg-gray-25'
                              }`}
                            >
                              <td className={`p-2 ${
                                item.isMainEntry 
                                  ? isDarkMode ? 'text-white font-semibold' : 'text-gray-900 font-semibold'
                                  : isDarkMode ? 'text-gray-300' : 'text-gray-700'
                              }`}>
                                <div className="flex items-center">
                                  {item.isMainEntry && (
                                    <span className="mr-2">🏢</span>
                                  )}
                                  {item.isSubEntry && (
                                    <span className="mr-2 text-gray-400">↳</span>
                                  )}
                                  {item.companyName}
                                </div>
                                <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                  {item.contactName}
                                </div>
                              </td>
                              <td className={`p-2 font-mono text-xs ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                                {item.phone}
                              </td>
                              <td className={`p-2 text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                {item.currentOperator}
                              </td>
                              <td className={`p-2 font-semibold text-xs ${
                                item.isMainEntry 
                                  ? isDarkMode ? 'text-green-400' : 'text-green-600'
                                  : isDarkMode ? 'text-blue-400' : 'text-blue-600'
                              }`}>
                                {item.newOperator}
                              </td>
                            </tr>
                          ))}
                          {previewData.length > 100 && (
                            <tr>
                              <td colSpan="4" className={`p-2 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                ... a dalších {previewData.length - 100} záznamů
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    
                    <div className={`mt-3 p-3 rounded-lg text-sm ${isDarkMode ? 'bg-blue-900/20 text-blue-200' : 'bg-blue-50 text-blue-800'}`}>
                      <div className="flex items-center mb-2">
                        <span className="mr-2">ℹ️</span>
                        <strong>Vysvětlení logiky:</strong>
                      </div>
                      <ul className="space-y-1 text-xs">
                        <li>🏢 <strong>Hlavní záznam:</strong> Firma s operátorem nalezeným v CSV</li>
                        <li>↳ <strong>Doplněné:</strong> Ostatní čísla u stejné firmy (pevné linky, další mobily)</li>
                        <li>📞 <strong>Všechna čísla</strong> u firmy dostanou stejného operátora</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Loading Overlay */}
        {isProcessing && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
            <div className={`p-6 rounded-lg shadow-lg max-w-sm w-full mx-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <div className="text-center">
                <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Zpracovávám...
                </h3>
                <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {loadingMessage}
                </p>
                <div className={`w-full bg-gray-200 rounded-full h-2.5 mb-2 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <div 
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${loadingProgress}%` }}
                  ></div>
                </div>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {loadingProgress}% dokončeno
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

